import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { useClubIsSellingPackages } from "@/features/Club/hooks/useClubIsSellingPackages";
import { navigationRef_atom } from "@/features/Navigation";
import { PackageType } from "@/features/Packages/CurrentPackagesModal/components/__generated__/ModalLoaderQuery.graphql";
import { sessionCurrentClubSlugAtom } from "@/features/Session";
import { SelectPaymentModal } from "@/features/Square/SelectPaymentModal";
import { useCreateUserCardOnFile } from "@/features/Square/SquareCardEntryModal/hooks/useCreateUserCardOnFile";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { SettingsButton } from "@/screens/Profile/components/SettingsButton";
import {
  CloseIconSmall,
  CreditCardIcon,
  MembershipIcon,
  PackagesIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useAssignPaymentToPackage } from "@whoosh/features/src/Packages";
import { SquareCard } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { useShouldShowCancelButton } from "../hooks/useShouldShowCancelButton";
import { CancelPackageModal } from "./CancelPackageModal";
import { PackageDetailActions_memberPackage$key } from "./__generated__/PackageDetailActions_memberPackage.graphql";

type Props = {
  packageRef: PackageDetailActions_memberPackage$key | null | undefined;
  type: PackageType;
};
export const PackageDetailActions = ({ packageRef, type }: Props) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const navigationRef = useAtomValue(navigationRef_atom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom);
  const { showToast } = useToast();
  const [isSelectPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false);
  const [isCancelPackageModalOpen, setIsCancelPackageModalOpen] =
    useState(false);
  const pkg = useFragment(PackageDetailActionsFragmentString, packageRef);
  const isMembership = pkg?.package?.__typename === "MembershipPackage";
  const customerId = pkg?.customer?.id || "";

  const shouldShowCancelButton = useShouldShowCancelButton(pkg);

  const [createSquareCard] = useCreateUserCardOnFile();
  const [assignPaymentMethodToPackage] = useAssignPaymentToPackage();

  const handlePaymentMethodSelected = (paymentMethodId: string) => {
    if (pkg) {
      assignPaymentMethodToPackage(
        { id: pkg.id, squareCardId: paymentMethodId },
        () => {
          setIsPaymentMethodModalOpen(false);
        },
        (error: any) => {
          setTimeout(() => {
            showToast({
              title: t`Error`,
              description: `${t`Unable to add payment method`} - ${
                error.message
              }`,
              semanticStyle: "error",
            });
          }, 10);
        }
      );
    }
  };

  const handleAddNewCard = (squareCard: SquareCard): Promise<string> =>
    createSquareCard({ squareNonce: squareCard.nonce });

  const onCancelPackageSuccess = () => {
    setIsCancelPackageModalOpen(false);
    showToast({
      title: t`Subscription Cancelled`,
      description: t`Your subscription has been successfully cancelled.`,
      semanticStyle: "success",
    });
  };

  const onCancelPackageError = () => {
    setIsCancelPackageModalOpen(false);
    showToast({
      title: t`Error Cancelling Subscription`,
      description: t`There was an error cancelling your subscription. Please contact the club.`,
      semanticStyle: "error",
    });
  };

  const isSellingPackages = useClubIsSellingPackages(pkg?.customer?.club);

  const onBrowsePress = () => {
    const screen = type === "MEMBERSHIP" ? "Memberships" : "Packages";

    if (clubSlug && navigationRef?.isReady()) {
      navigationRef.navigate("Club", {
        clubSlug,
        screen: "Store",
        params: { screen },
      });
      return;
    }

    navigation.navigate("Store", { screen });
  };

  return (
    <>
      <VStack>
        {isSellingPackages ? (
          <SettingsButton
            icon={isMembership ? <MembershipIcon /> : <PackagesIcon />}
            title={`${t`Browse Other`} ${
              isMembership ? t`Memberships` : t`Packages`
            }`}
            onPress={onBrowsePress}
            showDivider={shouldShowCancelButton}
          />
        ) : null}
        {shouldShowCancelButton ? (
          <SettingsButton
            icon={<CreditCardIcon />}
            title={t`Update Payment Method`}
            onPress={() => setIsPaymentMethodModalOpen(true)}
            showDivider
          />
        ) : null}
        {shouldShowCancelButton ? (
          <SettingsButton
            icon={<CloseIconSmall color="text-error" />}
            variant="danger"
            title={t`Cancel Subscription`}
            onPress={() => setIsCancelPackageModalOpen(true)}
          />
        ) : null}
      </VStack>
      {isSelectPaymentMethodModalOpen ? (
        <SelectPaymentModal
          customerId={customerId}
          isOpen={isSelectPaymentMethodModalOpen}
          onClose={() => setIsPaymentMethodModalOpen(false)}
          onAddNewCard={handleAddNewCard}
          onSelectPaymentMethod={handlePaymentMethodSelected}
        />
      ) : null}
      {isCancelPackageModalOpen ? (
        <CancelPackageModal
          isOpen={isCancelPackageModalOpen}
          onClose={() => setIsCancelPackageModalOpen(false)}
          memberPackageRef={pkg}
          onCancelSuccess={onCancelPackageSuccess}
          onCancelError={onCancelPackageError}
        />
      ) : null}
    </>
  );
};

const PackageDetailActionsFragmentString = graphql`
  fragment PackageDetailActions_memberPackage on MemberPackage {
    ...CancelPackageModal_memberPackage
    ...useShouldShowCancelButton_memberPackage
    id
    customer {
      id
      club {
        ...useClubIsSellingPackages_club
      }
    }
    package {
      __typename
      name
    }
  }
`;
